import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { shoe, shuffle, type Card } from '@campfire/cards';

/**
 * Card Table: a regular deck with no rules (FR-TABLE-*). Every mechanic the games use —
 * deal, draw, play face up or down, discard, pass cards, take the center, flip, turns — is a
 * plain action, and the players enforce whatever game they agree on (Hearts, Rummy, house rules).
 */
export interface TableOptions {
  readonly decks: 1 | 2;
  readonly jokers: 0 | 2;
  /** 'ordered': one player at a time, "End turn" passes it on. 'free': anyone may act any time. */
  readonly turns: 'ordered' | 'free';
}

export interface Played {
  readonly card: Card;
  readonly by: Seat;
  readonly faceDown: boolean;
}

export type TableEvent =
  | { readonly kind: 'gather'; readonly seat: Seat }
  | { readonly kind: 'deal'; readonly seat: Seat; readonly each: number; readonly left: number }
  | { readonly kind: 'draw'; readonly seat: Seat; readonly from: 'deck' | 'discard'; readonly card: Card | null }
  | { readonly kind: 'play'; readonly seat: Seat; readonly count: number; readonly cards: readonly Card[]; readonly faceDown: boolean }
  | { readonly kind: 'discard'; readonly seat: Seat; readonly cards: readonly Card[] }
  | { readonly kind: 'give'; readonly seat: Seat; readonly to: Seat; readonly count: number }
  | { readonly kind: 'take'; readonly seat: Seat; readonly into: 'won' | 'hand'; readonly count: number }
  | { readonly kind: 'flip'; readonly seat: Seat; readonly count: number }
  | { readonly kind: 'turn'; readonly seat: Seat; readonly to: Seat }
  | { readonly kind: 'dealer'; readonly seat: Seat; readonly to: Seat }
  | { readonly kind: 'finish'; readonly seat: Seat };

export interface TableState {
  readonly options: TableOptions;
  /** Face-down stock; index 0 is the top. */
  readonly deck: readonly Card[];
  /** Face-up discard pile; index 0 is the top. */
  readonly discard: readonly Card[];
  /** Cards played to the middle of the table, oldest first. */
  readonly center: readonly Played[];
  readonly hands: readonly (readonly Card[])[];
  /** Each player's face-down pile of taken cards (tricks, books…). */
  readonly won: readonly (readonly Card[])[];
  readonly dealer: Seat;
  readonly turn: Seat;
  /** Most recent last; capped. */
  readonly log: readonly TableEvent[];
  readonly result: Outcome | null;
}

export type TableAction =
  | { readonly type: 'gather' }
  | { readonly type: 'deal'; readonly count: number | 'all' }
  | { readonly type: 'draw'; readonly from: 'deck' | 'discard' }
  | { readonly type: 'play'; readonly cards: readonly string[]; readonly faceDown?: boolean }
  | { readonly type: 'discard'; readonly cards: readonly string[] }
  | { readonly type: 'give'; readonly cards: readonly string[]; readonly to: Seat }
  | { readonly type: 'take'; readonly into?: 'won' | 'hand' }
  | { readonly type: 'flip' }
  | { readonly type: 'endTurn'; readonly to?: Seat }
  | { readonly type: 'passDeal'; readonly to?: Seat }
  | { readonly type: 'finish'; readonly winners: readonly Seat[] };

export interface TableView {
  readonly you: Seat | null;
  readonly hand: readonly Card[] | null;
  readonly handCounts: readonly number[];
  readonly wonCounts: readonly number[];
  /** Your own taken pile. */
  readonly won: readonly Card[] | null;
  readonly deckCount: number;
  readonly discard: readonly Card[];
  /** Face-down cards show as null until flipped. */
  readonly center: readonly { readonly card: Card | null; readonly by: Seat }[];
  readonly dealer: Seat;
  readonly turn: Seat;
  readonly turns: TableOptions['turns'];
  readonly log: readonly TableEvent[];
  readonly result: Outcome | null;
}

const LOG_SIZE = 12;
const DEALER_ONLY = new Set(['gather', 'deal', 'passDeal', 'finish']);
const withLog = (s: TableState, e: TableEvent): TableState => ({ ...s, log: [...s.log, e].slice(-LOG_SIZE) });
const removeIds = (hand: readonly Card[], ids: readonly string[]) => {
  const set = new Set(ids);
  return { picked: hand.filter((c) => set.has(c.id)), rest: hand.filter((c) => !set.has(c.id)) };
};
const setAt = <T>(arr: readonly T[], i: number, v: T): T[] => arr.map((x, j) => (j === i ? v : x));

export const cardTable: GameDefinition<TableState, TableAction, TableView, TableOptions> = {
  id: 'table',
  name: 'Card Table',
  rules: [
    'Card Table is a bare deck with no rules enforced: the app deals and moves cards, and you play whatever game you agree on.',
    'The dealer can Deal (a number of cards each, or all of them; the dealer keeps the turn until End turn, e.g. to flip the top card), Gather everything back into a freshly shuffled deck, pass the deal and finish the game by naming the winners.',
    'Anyone can draw from the deck or from the discard pile, play cards to the middle face up or face down, discard, give cards to another player, and flip face-down cards.',
    'The middle can be taken into your own won pile (tricks, books) or into your hand.',
    'Setting: Turns. One at a time means the player on turn acts and taps End turn to pass it on; Anyone, any time lets everyone act at once.',
    'Setting: Decks (one or two) and Jokers (two per deck, or none).',
    'There is no scoring in the app. Agree on the game, who goes first, how points are kept, and when it is over; then the dealer taps Finish.',
    'Nothing is enforced. Not whose turn it is in a trick, not following suit, not hand sizes: that is all on the table.',
  ],
  minSeats: 1,
  maxSeats: 10,
  defaultOptions: { decks: 1, jokers: 0, turns: 'ordered' },
  optionSpecs: [
    {
      key: 'turns',
      label: 'Turns',
      help: 'No rules are enforced — agree on a game and play it.',
      choices: [
        { value: 'ordered', label: 'One at a time (End turn passes it on)' },
        { value: 'free', label: 'Anyone, any time' },
      ],
    },
    {
      key: 'decks',
      label: 'Decks',
      choices: [
        { value: 1, label: 'One deck' },
        { value: 2, label: 'Two decks' },
      ],
    },
    {
      key: 'jokers',
      label: 'Jokers',
      choices: [
        { value: 0, label: 'No jokers' },
        { value: 2, label: 'Two jokers per deck' },
      ],
    },
  ],

  setup({ seats, options, rng }) {
    return {
      options,
      deck: shuffle(shoe(options.decks, { jokers: options.jokers }), rng),
      discard: [],
      center: [],
      hands: Array.from({ length: seats }, () => []),
      won: Array.from({ length: seats }, () => []),
      dealer: 0,
      turn: 0,
      log: [],
      result: null,
    };
  },

  // The dealer can always deal, gather or finish; otherwise only the player on turn acts.
  activeSeats: (s) =>
    s.result ? [] : s.options.turns === 'free' ? s.hands.map((_, i) => i) : s.turn === s.dealer ? [s.turn] : [s.turn, s.dealer],

  validate(s, a, seat) {
    if (DEALER_ONLY.has(a.type)) {
      if (seat !== s.dealer) return { ok: false, reason: 'only the dealer can do that' };
    } else if (s.options.turns === 'ordered' && seat !== s.turn) {
      return { ok: false, reason: 'not your turn' };
    }
    const hand = s.hands[seat] ?? [];
    const holds = (ids: readonly string[] | undefined) =>
      Array.isArray(ids) && ids.length > 0 && new Set(ids).size === ids.length && ids.every((id) => hand.some((c) => c.id === id));
    switch (a.type) {
      case 'gather':
        return { ok: true };
      case 'deal':
        if (s.deck.length === 0) return { ok: false, reason: 'the deck is empty' };
        return a.count === 'all' || (Number.isInteger(a.count) && a.count >= 1 && a.count <= 52) ? { ok: true } : { ok: false, reason: 'deal 1–52 each, or all' };
      case 'draw':
        if (a.from === 'deck') return s.deck.length ? { ok: true } : { ok: false, reason: 'the deck is empty' };
        if (a.from === 'discard') return s.discard.length ? { ok: true } : { ok: false, reason: 'the discard pile is empty' };
        return { ok: false, reason: 'draw from the deck or the discard pile' };
      case 'play':
      case 'discard':
        return holds(a.cards) ? { ok: true } : { ok: false, reason: 'pick cards from your hand' };
      case 'give':
        if (!Number.isInteger(a.to) || a.to < 0 || a.to >= s.hands.length || a.to === seat) return { ok: false, reason: 'give to another player' };
        return holds(a.cards) ? { ok: true } : { ok: false, reason: 'pick cards from your hand' };
      case 'take':
        return s.center.length ? { ok: true } : { ok: false, reason: 'nothing in the middle' };
      case 'flip':
        return s.center.some((p) => p.faceDown) ? { ok: true } : { ok: false, reason: 'nothing face down in the middle' };
      case 'endTurn':
        if (s.options.turns === 'free') return { ok: false, reason: 'turns are free — just play' };
        if (a.to !== undefined && (!Number.isInteger(a.to) || a.to < 0 || a.to >= s.hands.length)) return { ok: false, reason: 'no such player' };
        return { ok: true };
      case 'passDeal':
        if (s.hands.length < 2) return { ok: false, reason: 'nobody to pass the deal to' };
        if (a.to !== undefined && (!Number.isInteger(a.to) || a.to < 0 || a.to >= s.hands.length || a.to === seat)) return { ok: false, reason: 'pass the deal to another player' };
        return { ok: true };
      case 'finish':
        return Array.isArray(a.winners) && a.winners.every((w) => Number.isInteger(w) && w >= 0 && w < s.hands.length)
          ? { ok: true }
          : { ok: false, reason: 'pick the winners' };
      default:
        return { ok: false, reason: 'unknown action' };
    }
  },

  apply(s, a, { actor, rng }) {
    const seat = actor as Seat;
    const hand = s.hands[seat] as readonly Card[];
    switch (a.type) {
      case 'gather': {
        const all = [...s.deck, ...s.discard, ...s.center.map((p) => p.card), ...s.hands.flat(), ...s.won.flat()];
        return withLog(
          { ...s, deck: shuffle(all, rng), discard: [], center: [], hands: s.hands.map(() => []), won: s.won.map(() => []), turn: s.dealer },
          { kind: 'gather', seat },
        );
      }
      case 'deal': {
        const n = s.hands.length;
        const total = a.count === 'all' ? s.deck.length : Math.min(s.deck.length, a.count * n);
        const hands = s.hands.map((h) => [...h]);
        for (let i = 0; i < total; i++) (hands[(s.dealer + 1 + i) % n] as Card[]).push(s.deck[i] as Card);
        return withLog(
          // The dealer keeps the turn (e.g. to flip the top card); End turn passes it on (FR-TABLE-09).
          { ...s, hands, deck: s.deck.slice(total) },
          { kind: 'deal', seat, each: Math.floor(total / n), left: s.deck.length - total },
        );
      }
      case 'draw': {
        const pile = a.from === 'deck' ? s.deck : s.discard;
        const card = pile[0] as Card;
        const next = { ...s, hands: setAt(s.hands, seat, [...hand, card]) };
        return withLog(a.from === 'deck' ? { ...next, deck: s.deck.slice(1) } : { ...next, discard: s.discard.slice(1) }, {
          kind: 'draw',
          seat,
          from: a.from,
          card: a.from === 'discard' ? card : null,
        });
      }
      case 'play': {
        const { picked, rest } = removeIds(hand, a.cards);
        const faceDown = !!a.faceDown;
        return withLog(
          { ...s, hands: setAt(s.hands, seat, rest), center: [...s.center, ...picked.map((card) => ({ card, by: seat, faceDown }))] },
          { kind: 'play', seat, count: picked.length, cards: faceDown ? [] : picked, faceDown },
        );
      }
      case 'discard': {
        const { picked, rest } = removeIds(hand, a.cards);
        return withLog({ ...s, hands: setAt(s.hands, seat, rest), discard: [...[...picked].reverse(), ...s.discard] }, { kind: 'discard', seat, cards: picked });
      }
      case 'give': {
        const { picked, rest } = removeIds(hand, a.cards);
        const hands = setAt(setAt(s.hands, seat, rest), a.to, [...(s.hands[a.to] as readonly Card[]), ...picked]);
        return withLog({ ...s, hands }, { kind: 'give', seat, to: a.to, count: picked.length });
      }
      case 'take': {
        const into = a.into ?? 'won';
        const cards = s.center.map((p) => p.card);
        const next =
          into === 'won' ? { ...s, won: setAt(s.won, seat, [...(s.won[seat] as readonly Card[]), ...cards]) } : { ...s, hands: setAt(s.hands, seat, [...hand, ...cards]) };
        return withLog({ ...next, center: [] }, { kind: 'take', seat, into, count: cards.length });
      }
      case 'flip':
        return withLog({ ...s, center: s.center.map((p) => ({ ...p, faceDown: false })) }, { kind: 'flip', seat, count: s.center.filter((p) => p.faceDown).length });
      case 'endTurn': {
        const to = a.to ?? (s.turn + 1) % s.hands.length;
        return withLog({ ...s, turn: to }, { kind: 'turn', seat, to });
      }
      case 'passDeal': {
        // Like the button in poker: the deal moves on (clockwise by default) and the new dealer has the turn.
        const to = a.to ?? (s.dealer + 1) % s.hands.length;
        return withLog({ ...s, dealer: to, turn: to }, { kind: 'dealer', seat, to });
      }
      case 'finish': {
        const winners = [...new Set(a.winners)].sort((x, y) => x - y);
        return withLog({ ...s, result: { winners, draw: winners.length === 0, reason: 'declared' } }, { kind: 'finish', seat });
      }
    }
  },

  view(s, viewer: Viewer) {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    return {
      you,
      hand: you === null ? null : (s.hands[you] ?? null),
      handCounts: s.hands.map((h) => h.length),
      wonCounts: s.won.map((w) => w.length),
      won: you === null ? null : (s.won[you] ?? null),
      deckCount: s.deck.length,
      discard: s.discard,
      center: s.center.map((p) => ({ card: p.faceDown ? null : p.card, by: p.by })),
      dealer: s.dealer,
      turn: s.turn,
      turns: s.options.turns,
      // A card drawn from the deck is private to the drawer.
      log: s.log.map((e) => (e.kind === 'draw' && e.seat !== you && e.from === 'deck' ? { ...e, card: null } : e)),
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result) return [];
    const out: TableAction[] = [];
    if (seat === s.dealer) {
      out.push({ type: 'gather' }, { type: 'finish', winners: [seat] });
      if (s.hands.length > 1) out.push({ type: 'passDeal' });
      if (s.deck.length) out.push({ type: 'deal', count: 'all' }, { type: 'deal', count: 5 });
    }
    if (s.options.turns === 'ordered' && seat !== s.turn) return out;
    const hand = s.hands[seat] ?? [];
    const first = hand[0];
    if (s.deck.length) out.push({ type: 'draw', from: 'deck' });
    if (s.discard.length) out.push({ type: 'draw', from: 'discard' });
    if (first) {
      out.push({ type: 'play', cards: [first.id] }, { type: 'play', cards: [first.id], faceDown: true }, { type: 'discard', cards: [first.id] });
      if (s.hands.length > 1) out.push({ type: 'give', cards: [first.id], to: (seat + 1) % s.hands.length });
    }
    if (s.center.length) out.push({ type: 'take' }, { type: 'take', into: 'hand' });
    if (s.center.some((p) => p.faceDown)) out.push({ type: 'flip' });
    if (s.options.turns === 'ordered') out.push({ type: 'endTurn' });
    return out;
  },
};
